import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { UserRole } from 'domain/user';
import { useIsSwUser } from 'hooks/useIsSwUser';

import { Tooltip } from '../../../../components/Tooltip';
import type { Crm } from '../../../../domain/crm';
import { useUserQuery } from '../../../../features/user/useUserQuery';
import { translate } from '../../../../services/i18n';
import { StyledCrmExportButton, StyledCrmExportBulkButton } from '../CrmExportContainer/styles';

import { StyledCrmExportButtonLink } from './styles';

export const InactiveCrmExportButton = ({
  crm,
  handleEnableExportTrack,
  variant = 'single',
  isDisabled = false,
}: {
  crm?: Crm;
  handleEnableExportTrack: (action: string, userRole: UserRole) => void;
  variant?: 'single' | 'bulk';
  isDisabled?: boolean;
}) => {
  const userQuery = useUserQuery();
  const hasAccessToManageCrm = userQuery.data?.hasAccessToManageCrm ?? false;
  const userRole = userQuery.data?.userRole ?? UserRole.USER;

  const isSwUser = useIsSwUser();
  const accountAdminEmails = userQuery.data?.accountAdminEmails ?? [];
  const showAdminEmails = !isSwUser && accountAdminEmails.length > 0 && !hasAccessToManageCrm;

  let tooltipContent = translate(
    hasAccessToManageCrm ? 'contact_card.export_crm.disabled.tooltip_admin' : 'contact_card.export_crm.disabled.tooltip'
  );
  if (showAdminEmails) {
    tooltipContent += ` - ${accountAdminEmails.join(', ')}`;
  }

  const handleExportClick = () => handleEnableExportTrack('export not enabled', userRole);

  const renderContent = () => {
    if (hasAccessToManageCrm) {
      return (
        <StyledCrmExportButtonLink
          target="_blank"
          href="https://pro.similarweb.com/#/sales/management"
          onMouseDown={handleExportClick}
        >
          {variant === 'single' && !!crm ? (
            <SWReactIcons iconName={crm} size="xs" iconColor={colorsPalettes.carbon[500]} />
          ) : null}
          {translate('contact_card.export_crm')}
        </StyledCrmExportButtonLink>
      );
    }
    return (
      <>
        {variant === 'single' && !!crm ? (
          <SWReactIcons iconName={crm} size="xs" iconColor={colorsPalettes.carbon[200]} />
        ) : null}
        {translate('contact_card.export_crm')}
      </>
    );
  };

  return (
    <Tooltip
      content={tooltipContent}
      placement={variant === 'bulk' ? 'top' : 'top-left'}
      minWidth={showAdminEmails ? 220 : 210}
      enabled={!isDisabled}
    >
      {variant === 'single' ? (
        <StyledCrmExportButton
          onClick={handleExportClick}
          data-automation="CrmExportInactiveButton"
          disabled={isDisabled || !hasAccessToManageCrm}
        >
          {renderContent()}
        </StyledCrmExportButton>
      ) : (
        <StyledCrmExportBulkButton
          variant="flat"
          data-automation="CrmExportInactiveButtonBulk"
          disabled={isDisabled || !hasAccessToManageCrm}
          iconName={crm ? crm : 'chev-down'}
          iconSize="xs"
          placement={crm ? 'left' : 'right'}
          onClick={handleExportClick}
        >
          {renderContent()}
        </StyledCrmExportBulkButton>
      )}
    </Tooltip>
  );
};
